import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft,
  Loader2,
  Sparkles,
  Video,
  Play,
  Download,
  ExternalLink,
  RefreshCw,
  Lock,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "nztx_admin_passcode";

/**
 * /admin/videos — HeyGen + Claude-powered marketing video generator.
 * Passcode-gated (no full auth). Flow:
 *   1. Enter topic → Claude drafts a narration script
 *   2. Pick avatar + voice from HeyGen catalog
 *   3. Render → poll status → play/download the finished video
 */
export default function AdminVideosPage() {
  const [passcode, setPasscode] = useState(() => localStorage.getItem(STORAGE_KEY) || "");
  const [authed, setAuthed] = useState(!!localStorage.getItem(STORAGE_KEY));

  if (!authed) {
    return <Gate onUnlock={(p) => { localStorage.setItem(STORAGE_KEY, p); setPasscode(p); setAuthed(true); }} />;
  }
  return <Studio passcode={passcode} onLogout={() => { localStorage.removeItem(STORAGE_KEY); setAuthed(false); setPasscode(""); }} />;
}

function Gate({ onUnlock }) {
  const [value, setValue] = useState("");
  return (
    <main className="min-h-screen grid place-items-center bg-[#0A192F] text-white px-6">
      <form
        onSubmit={(e) => { e.preventDefault(); if (value.trim()) onUnlock(value.trim()); }}
        className="w-full max-w-sm bg-white text-[#0F172A] p-8 border-l-4 border-[#C9A349]"
      >
        <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-bold text-[#C9A349]">
          <Lock className="w-3 h-3" /> Admin only
        </div>
        <h1 className="mt-4 font-heading font-black text-3xl tracking-tight">Video studio</h1>
        <p className="mt-2 text-slate-600 text-sm">Enter the admin passcode to continue.</p>
        <input
          type="password"
          autoFocus
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="mt-6 w-full border border-slate-300 px-3 py-3 focus:outline-none focus:border-[#C9A349]"
          data-testid="admin-passcode-input"
        />
        <button
          type="submit"
          className="mt-4 w-full bg-[#0A192F] text-white py-3 font-heading font-bold uppercase tracking-[0.14em] text-sm hover:bg-[#C9A349] hover:text-[#0A192F] transition-colors"
          data-testid="admin-passcode-submit"
        >
          Unlock
        </button>
      </form>
    </main>
  );
}

function Studio({ passcode, onLogout }) {
  const auth = useMemo(() => ({ headers: { "X-Admin-Passcode": passcode } }), [passcode]);

  const [topic, setTopic] = useState("");
  const [duration, setDuration] = useState(60);
  const [script, setScript] = useState("");
  const [scriptLoading, setScriptLoading] = useState(false);

  const [avatars, setAvatars] = useState([]);
  const [voices, setVoices] = useState([]);
  const [avatarId, setAvatarId] = useState("");
  const [voiceId, setVoiceId] = useState("");
  const [avatarFilter, setAvatarFilter] = useState("");
  const [voiceFilter, setVoiceFilter] = useState("");
  const [catalogLoading, setCatalogLoading] = useState(false);

  const [title, setTitle] = useState("");
  const [rendering, setRendering] = useState(false);
  const [videos, setVideos] = useState([]);
  const pollRef = useRef(null);

  // Load HeyGen catalog + existing videos on mount
  useEffect(() => {
    (async () => {
      setCatalogLoading(true);
      try {
        const [av, vo, list] = await Promise.all([
          axios.get(`${API}/admin/videos/avatars`, auth),
          axios.get(`${API}/admin/videos/voices`, auth),
          axios.get(`${API}/admin/videos`, auth),
        ]);
        setAvatars(av.data.avatars || []);
        setVoices(vo.data.voices || []);
        setVideos(list.data.videos || []);
      } catch (e) {
        if (e?.response?.status === 401) {
          toast.error("Passcode rejected — logged out.");
          onLogout();
        } else {
          toast.error("Failed to load HeyGen catalog: " + (e?.response?.data?.detail || e.message));
        }
      } finally {
        setCatalogLoading(false);
      }
    })();
  }, []);

  // Poll processing videos every 8s
  useEffect(() => {
    const poll = async () => {
      const processing = videos.filter((v) => v.status === "processing" || v.status === "pending");
      if (processing.length === 0) return;
      for (const v of processing) {
        try {
          const r = await axios.get(`${API}/admin/videos/${v.id}/status`, auth);
          setVideos((prev) => prev.map((x) => (x.id === v.id ? r.data : x)));
          if (r.data.status === "completed") toast.success(`"${r.data.title}" is ready`);
          if (r.data.status === "failed") toast.error(`"${r.data.title}" failed: ${r.data.error || "unknown error"}`);
        } catch { /* ignore transient poll errors */ }
      }
    };
    pollRef.current = setInterval(poll, 8000);
    return () => clearInterval(pollRef.current);
  }, [videos, auth]);

  const draftScript = async () => {
    if (!topic.trim()) return toast.info("Give me a topic first.");
    setScriptLoading(true);
    try {
      const r = await axios.post(`${API}/admin/videos/script`, {
        topic,
        duration_seconds: duration,
      }, auth);
      setScript(r.data.script);
      if (!title) setTitle(topic.slice(0, 80));
      toast.success(`Script drafted (~${r.data.word_count} words)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Script generation failed");
    } finally {
      setScriptLoading(false);
    }
  };

  const render = async () => {
    if (!script.trim()) return toast.info("Draft or paste a script first.");
    if (!avatarId) return toast.info("Pick an avatar.");
    if (!voiceId) return toast.info("Pick a voice.");
    setRendering(true);
    try {
      const r = await axios.post(`${API}/admin/videos/generate`, {
        title: title.trim() || topic.slice(0, 80) || "Untitled video",
        script,
        avatar_id: avatarId,
        voice_id: voiceId,
      }, auth);
      setVideos((prev) => [r.data, ...prev]);
      toast.success("Rendering started — HeyGen usually takes 1-3 minutes.");
      // Optional: reset form
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Render failed");
    } finally {
      setRendering(false);
    }
  };

  const filteredAvatars = useMemo(() => {
    const q = avatarFilter.toLowerCase();
    return avatars.filter((a) => !q || (a.name || "").toLowerCase().includes(q)).slice(0, 60);
  }, [avatars, avatarFilter]);

  const filteredVoices = useMemo(() => {
    const q = voiceFilter.toLowerCase();
    // Default: English only unless the user is filtering something specific
    const englishOnly = !q;
    return voices
      .filter((v) => {
        if (englishOnly) return (v.language || "").toLowerCase().includes("english");
        return (
          (v.name || "").toLowerCase().includes(q) ||
          (v.language || "").toLowerCase().includes(q) ||
          (v.gender || "").toLowerCase().includes(q)
        );
      })
      .slice(0, 60);
  }, [voices, voiceFilter]);

  return (
    <main className="min-h-screen bg-[#FAFAFA] text-[#0F172A]" data-testid="admin-videos-page">
      <div className="border-b border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-6 flex items-center justify-between gap-4">
          <div>
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-bold text-slate-500 hover:text-[#C9A349] transition-colors"
            >
              <ArrowLeft className="w-3 h-3" /> nztaxation.nz
            </Link>
            <h1 className="mt-2 font-heading font-black text-2xl md:text-3xl tracking-tight">
              Video studio
            </h1>
          </div>
          <button
            onClick={onLogout}
            className="text-xs uppercase tracking-[0.18em] font-bold text-slate-500 hover:text-[#C9A349]"
            data-testid="admin-logout"
          >
            Lock studio
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 md:px-10 py-10 md:py-14 grid md:grid-cols-12 gap-8">
        {/* Left column: builder */}
        <section className="md:col-span-7 space-y-6">
          {/* Topic + script */}
          <div className="bg-white border-l-4 border-[#C9A349] px-6 md:px-7 py-6">
            <SectionKicker n="1" label="Draft the script" />
            <textarea
              rows={3}
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder="e.g. Why co-owned rentals need two IR3s and what the extra $100 fee covers"
              className="mt-4 w-full border border-slate-300 px-3 py-3 focus:outline-none focus:border-[#C9A349]"
              data-testid="video-topic"
            />
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <label className="text-xs uppercase tracking-[0.18em] font-bold text-slate-600">
                Target length
              </label>
              <select
                value={duration}
                onChange={(e) => setDuration(Number(e.target.value))}
                className="border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-[#C9A349]"
                data-testid="video-duration"
              >
                <option value={30}>30 sec</option>
                <option value={45}>45 sec</option>
                <option value={60}>60 sec</option>
                <option value={75}>75 sec</option>
                <option value={90}>90 sec</option>
                <option value={120}>2 min</option>
              </select>
              <button
                onClick={draftScript}
                disabled={scriptLoading}
                className="ml-auto inline-flex items-center gap-2 bg-[#0A192F] text-white px-5 py-2.5 font-heading font-bold uppercase tracking-[0.14em] text-xs hover:bg-[#C9A349] hover:text-[#0A192F] transition-colors disabled:opacity-50"
                data-testid="video-draft-btn"
              >
                {scriptLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                Draft with Claude
              </button>
            </div>

            <textarea
              rows={7}
              value={script}
              onChange={(e) => setScript(e.target.value)}
              placeholder="Your narration will appear here. You can edit it freely before rendering."
              className="mt-5 w-full border border-slate-300 px-3 py-3 text-sm focus:outline-none focus:border-[#C9A349]"
              data-testid="video-script"
            />
            <div className="mt-1 text-[11px] text-slate-500">
              {script.split(/\s+/).filter(Boolean).length} words · ~{Math.round(script.split(/\s+/).filter(Boolean).length / 2.3)}s spoken
            </div>
          </div>

          {/* Avatar + voice */}
          <div className="bg-white border-l-4 border-[#C9A349] px-6 md:px-7 py-6">
            <SectionKicker n="2" label="Pick a presenter" />

            <div className="mt-4 grid md:grid-cols-2 gap-6">
              <PickerColumn
                title="Avatar"
                filter={avatarFilter}
                onFilter={setAvatarFilter}
                items={filteredAvatars}
                selected={avatarId}
                onSelect={(a) => setAvatarId(a.avatar_id)}
                renderItem={(a) => (
                  <div className="flex items-center gap-3">
                    {a.preview_image_url ? (
                      <img src={a.preview_image_url} alt="" className="w-10 h-10 object-cover" loading="lazy" />
                    ) : (
                      <div className="w-10 h-10 bg-slate-200" />
                    )}
                    <div className="min-w-0">
                      <div className="text-sm font-heading font-bold truncate">{a.name}</div>
                      <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">{a.gender}</div>
                    </div>
                  </div>
                )}
                loading={catalogLoading}
                emptyMsg="No avatars"
                testId="avatar-picker"
              />
              <PickerColumn
                title="Voice"
                filter={voiceFilter}
                onFilter={setVoiceFilter}
                items={filteredVoices}
                selected={voiceId}
                onSelect={(v) => setVoiceId(v.voice_id)}
                renderItem={(v) => (
                  <div className="min-w-0">
                    <div className="text-sm font-heading font-bold truncate">{v.name || "(unnamed)"}</div>
                    <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">
                      {v.language} · {v.gender}
                    </div>
                  </div>
                )}
                loading={catalogLoading}
                emptyMsg="No voices"
                testId="voice-picker"
                extraHint={!voiceFilter ? "Showing English voices — type to search all languages" : null}
              />
            </div>
          </div>

          {/* Render */}
          <div className="bg-white border-l-4 border-[#C9A349] px-6 md:px-7 py-6">
            <SectionKicker n="3" label="Render" />
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Video title (internal reference)"
              className="mt-4 w-full border border-slate-300 px-3 py-3 focus:outline-none focus:border-[#C9A349]"
              data-testid="video-title"
            />
            <button
              onClick={render}
              disabled={rendering || !script.trim() || !avatarId || !voiceId}
              className="mt-4 inline-flex items-center gap-2 bg-[#C9A349] text-[#0A192F] px-6 py-3.5 font-heading font-black uppercase tracking-[0.14em] text-sm hover:bg-[#0A192F] hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="video-render-btn"
            >
              {rendering ? <Loader2 className="w-4 h-4 animate-spin" /> : <Video className="w-4 h-4" />}
              Render with HeyGen
            </button>
            <p className="mt-3 text-xs text-slate-500">
              HeyGen usually finishes in 1-3 minutes. Videos appear in the library on the right —
              status polls automatically every 8 seconds.
            </p>
          </div>
        </section>

        {/* Right column: library */}
        <aside className="md:col-span-5">
          <div className="bg-white border border-slate-200 px-6 py-5 sticky top-6">
            <div className="flex items-center justify-between">
              <h2 className="font-heading font-black text-lg tracking-tight">Video library</h2>
              <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500">{videos.length}</span>
            </div>
            <ul className="mt-4 space-y-3 max-h-[70vh] overflow-y-auto pr-1" data-testid="video-library">
              {videos.length === 0 && (
                <li className="text-sm text-slate-500 italic border border-dashed border-slate-300 px-4 py-6 text-center">
                  Your rendered videos will show up here.
                </li>
              )}
              {videos.map((v) => (
                <VideoCard key={v.id} v={v} />
              ))}
            </ul>
          </div>
        </aside>
      </div>
    </main>
  );
}

function SectionKicker({ n, label }) {
  return (
    <div className="inline-flex items-center gap-3">
      <span className="w-7 h-7 grid place-items-center bg-[#0A192F] text-[#C9A349] font-heading font-black text-sm">
        {n}
      </span>
      <span className="text-[11px] uppercase tracking-[0.22em] font-bold text-slate-600">{label}</span>
    </div>
  );
}

function PickerColumn({ title, filter, onFilter, items, selected, onSelect, renderItem, loading, emptyMsg, testId, extraHint }) {
  return (
    <div>
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-600">{title}</div>
        {loading && <Loader2 className="w-3 h-3 animate-spin text-slate-400" />}
      </div>
      <input
        type="text"
        value={filter}
        onChange={(e) => onFilter(e.target.value)}
        placeholder={`Search ${title.toLowerCase()}…`}
        className="mt-2 w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-[#C9A349]"
      />
      {extraHint && <div className="mt-1 text-[10px] text-slate-500">{extraHint}</div>}
      <ul className="mt-2 border border-slate-200 max-h-64 overflow-y-auto" data-testid={testId}>
        {items.length === 0 && !loading && (
          <li className="text-sm text-slate-500 italic px-3 py-3">{emptyMsg}</li>
        )}
        {items.map((it) => {
          const id = it.avatar_id || it.voice_id;
          const isSel = selected === id;
          return (
            <li
              key={id}
              onClick={() => onSelect(it)}
              className={`px-3 py-2 cursor-pointer border-b border-slate-100 last:border-0 ${
                isSel ? "bg-[#C9A349]/15 border-l-2 border-l-[#C9A349]" : "hover:bg-slate-50"
              }`}
            >
              {renderItem(it)}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function VideoCard({ v }) {
  const isDone = v.status === "completed" && v.video_url;
  const isFailed = v.status === "failed";
  return (
    <li className="border border-slate-200 p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-heading font-bold truncate">{v.title}</div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 mt-0.5">
            {v.status}
            {v.duration ? ` · ${Math.round(v.duration)}s` : ""}
          </div>
        </div>
        {v.status === "processing" && <Loader2 className="w-4 h-4 animate-spin text-[#C9A349] flex-shrink-0" />}
        {isFailed && <span className="text-red-500 text-xs flex-shrink-0">✕</span>}
      </div>
      {isDone && (
        <>
          {v.thumbnail_url && (
            <img src={v.thumbnail_url} alt="" className="mt-3 w-full aspect-video object-cover" />
          )}
          <div className="mt-2 flex flex-wrap gap-2">
            <a
              href={v.video_url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 bg-[#0A192F] text-white text-[11px] uppercase tracking-[0.14em] font-bold px-3 py-1.5 hover:bg-[#C9A349] hover:text-[#0A192F]"
            >
              <Play className="w-3 h-3" /> Play
            </a>
            <a
              href={v.video_url}
              download={`${v.title.replace(/[^a-z0-9]+/gi, "_")}.mp4`}
              className="inline-flex items-center gap-1 border border-slate-300 text-[11px] uppercase tracking-[0.14em] font-bold px-3 py-1.5 hover:border-[#C9A349]"
            >
              <Download className="w-3 h-3" /> MP4
            </a>
            <a
              href={v.video_url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.14em] font-bold text-slate-500 hover:text-[#C9A349] px-2 py-1.5"
            >
              <ExternalLink className="w-3 h-3" /> Direct link
            </a>
          </div>
        </>
      )}
      {isFailed && v.error && (
        <div className="mt-2 text-xs text-red-500 border-l-2 border-red-500 pl-2">{v.error}</div>
      )}
      {v.status === "processing" && (
        <div className="mt-2 text-[10px] text-slate-500 inline-flex items-center gap-1">
          <RefreshCw className="w-3 h-3" /> Auto-refreshing every 8s
        </div>
      )}
    </li>
  );
}
